import { FIRST_APPROACH, VIEW_CLOSE, VIEW_CLOSEST, VIEW_DENSE, VIEW_OVERVIEW, VIEW_START } from "./renderBenchmark";
import type { ArrivalMeasure, RenderBenchmarkResult } from "./renderBenchmark";

/** Le temps de réponse d'une action du visiteur : ouvrir une fiche, chercher, calculer un itinéraire. */
export interface ReactivityMeasure {
  id: "entity" | "search" | "route";
  label: string;
  ms: number;
}

/** Ce que la carte occupe en mémoire, quand on sait l'estimer. */
export interface MemoryMeasure {
  /** Géométries et textures du décor 3D envoyées au GPU, estimées. */
  decorBytes: number | null;
  /** Tas JavaScript de la page (Chrome seulement). */
  jsHeapBytes: number | null;
}

export type BenchmarkGrade = "Excellente" | "Bonne" | "Correcte" | "Lourde" | "Très lourde";

/** Un point à améliorer : ce que l'auteur de la carte peut y faire, et ce qu'en montrent les mesures. */
export interface BenchmarkAdvice {
  title: string;
  /** Pour l'auteur de la carte, sans jargon. */
  detail: string;
  /** Pour l'équipe technique : les chiffres qui fondent le conseil. */
  technical: string;
}

/** Une part de la note : naviguer dans la carte, ou y arriver. */
export interface BenchmarkPart {
  label: string;
  /** De 0 à 100. */
  score: number;
}

export interface BenchmarkVerdict {
  /** De 0 à 100. */
  score: number;
  /** Ce dont la note est faite, quand elle mêle plusieurs parts. */
  parts: BenchmarkPart[];
  grade: BenchmarkGrade;
  /** Une phrase, compréhensible par n'importe qui. */
  summary: string;
  /** Du plus au moins coûteux. */
  advice: BenchmarkAdvice[];
  /** Ce qu'il faut savoir pour lire la note : une arrivée estimée, des ressources venues du cache. */
  notes: string[];
}

/** Le poids de chaque geste dans la note de navigation : se déplacer et zoomer d'abord, de près comme de loin. */
const SCENARIO_WEIGHTS: { view: string; scenario: string; weight: number }[] = [
  { view: VIEW_START, scenario: "Déplacement", weight: 0.1 },
  { view: VIEW_START, scenario: "Zoom", weight: 0.1 },
  { view: VIEW_START, scenario: "Rotation", weight: 0.05 },
  { view: VIEW_START, scenario: "Immobile", weight: 0.05 },
  { view: VIEW_START, scenario: "Inclinaison", weight: 0.05 },
  { view: VIEW_START, scenario: "Déplacement incliné", weight: 0.05 },
  { view: VIEW_START, scenario: "Traversée", weight: 0.05 },
  { view: VIEW_START, scenario: FIRST_APPROACH, weight: 0.05 },
  { view: VIEW_OVERVIEW, scenario: "Déplacement", weight: 0.05 },
  { view: VIEW_OVERVIEW, scenario: "Rotation", weight: 0.05 },
  { view: VIEW_OVERVIEW, scenario: "Grand zoom", weight: 0.1 },
  { view: VIEW_CLOSE, scenario: "Déplacement", weight: 0.05 },
  { view: VIEW_CLOSE, scenario: "Rotation", weight: 0.05 },
  { view: VIEW_DENSE, scenario: "Déplacement", weight: 0.05 },
  { view: VIEW_CLOSEST, scenario: "Déplacement", weight: 0.1 },
  { view: VIEW_CLOSEST, scenario: "Rotation", weight: 0.05 },
];
/** Les gestes les plus saccadés, comptés à part dans la note de navigation, et leur poids face à la moyenne. */
const WORST_GESTURES = 3;
const WORST_GESTURES_WEIGHT = 0.5;
/** Le poids de chaque part dans la note, ramené aux parts mesurées : on navigue tout le temps, on arrive une fois. */
const PART_WEIGHTS = { navigation: 0.6, arrival: 0.25, reactivity: 0.15 };
/**
 * Par action : en deçà de `fast`, aucune attente perceptible ; au-delà de `slow`, elle ne vaut plus rien ;
 * au-delà de `advise`, elle mérite un conseil. Une fiche ou une recherche doivent répondre aussitôt ;
 * un itinéraire d'un bout à l'autre de la carte (le cas mesuré), on accepte de l'attendre un peu.
 */
const ACTION_LIMITS: Record<ReactivityMeasure["id"], { fast: number; slow: number; advise: number }> = {
  entity: { fast: 100, slow: 1000, advise: 300 },
  search: { fast: 100, slow: 1000, advise: 300 },
  route: { fast: 300, slow: 3000, advise: 1500 },
};
/** Au-delà, le fond de carte met trop longtemps à s'afficher quand on s'approche. */
const LONG_SETTLE_MS = 4000;
/** Au-delà, la page risque d'être fermée par un navigateur de téléphone (Safari sur iPhone, vers 1 Go). */
const HEAVY_DECOR_BYTES = 400 * 1024 * 1024;
const HEAVY_HEAP_BYTES = 800 * 1024 * 1024;
/** Gain, en images par seconde, en deçà duquel masquer un groupe de couches ne change rien de sensible. */
const MEANINGFUL_GAIN_FPS = 4;
/** Au-delà, l'herbe et les cailloux semés peuvent peser à eux seuls (voir `SCATTER_BUDGET`). */
const HEAVY_SCATTER = 40_000;
/** Fluidité à partir de laquelle un geste ne justifie aucun conseil. */
const SMOOTH_GESTURE = 0.85;

/** En deçà, le chargement est sans reproche ; au-delà de `SLOW_LOAD_MS`, il ne vaut plus rien. */
const FAST_LOAD_MS = 2000;
const SLOW_LOAD_MS = 10_000;
/** Au-delà, un chargement mérite un conseil. */
const LONG_LOAD_MS = 5000;

const clamp01 = (value: number) => Math.max(0, Math.min(1, value));

/** Durée supposée d'une mesure qui ne dit pas la sienne. */
const DEFAULT_MEASURE_MS = 3000;
/** Au-delà, les images lentes se succèdent assez pour que le geste paraisse haché de bout en bout. */
const HITCHES_PER_SECOND_MAX = 1.5;
/** Au-delà de cette part du temps passée figée, le geste ne vaut plus rien côté à-coups. */
const FROZEN_SHARE_MAX = 0.1;

/**
 * À-coups d'un geste, de 0 (haché) à 1 (aucun) : la fréquence des images lentes, la part du temps
 * figée et la pire image. C'est ce que l'œil retient, bien plus que la cadence moyenne.
 */
export function smoothness(result: RenderBenchmarkResult): number {
  const seconds = (result.durationMs || DEFAULT_MEASURE_MS) / 1000;
  const hitches = clamp01(1 - result.slowFrames / seconds / HITCHES_PER_SECOND_MAX);
  const frozen = clamp01(1 - result.longTasksMs / (seconds * 1000) / FROZEN_SHARE_MAX);
  const worst = clamp01(1 - (result.worstFrameMs - 50) / 450);
  return hitches * 0.4 + frozen * 0.3 + worst * 0.3;
}

/** Fluidité d'un geste, de 0 à 1 : la cadence habituelle, celle des moments lents, et surtout les à-coups. */
export function fluidity(result: RenderBenchmarkResult): number {
  return clamp01((result.fpsMedian - 20) / 40) * 0.4
    + clamp01((result.fpsLow - 15) / 45) * 0.2
    + smoothness(result) * 0.4;
}

export function loadScore(loadMs: number): number {
  return clamp01(1 - (loadMs - FAST_LOAD_MS) / (SLOW_LOAD_MS - FAST_LOAD_MS));
}

/**
 * L'arrivée, de 0 à 1 : le temps de chargement, et la fluidité des premières secondes, vol d'arrivée
 * compris quand il est actif. `null` quand elle n'a pas été mesurée, ou fenêtre masquée.
 */
export function arrivalScore(arrival: ArrivalMeasure | null): number | null {
  if (!arrival || arrival.window.throttled) return null;
  return loadScore(arrival.loadMs) * 0.4 + fluidity(arrival.window) * 0.6;
}

export function gradeOf(score: number): BenchmarkGrade {
  if (score >= 85) return "Excellente";
  if (score >= 70) return "Bonne";
  if (score >= 55) return "Correcte";
  if (score >= 40) return "Lourde";
  return "Très lourde";
}

const SUMMARIES: Record<BenchmarkGrade, string> = {
  "Excellente": "La carte est parfaitement fluide : on la parcourt sans aucune gêne.",
  "Bonne": "La carte est fluide dans l'ensemble ; quelques ralentissements passagers peuvent se sentir.",
  "Correcte": "La carte reste utilisable, mais elle saccade quand on la déplace ou qu'on zoome.",
  "Lourde": "La carte est lourde : déplacements et zoom saccadent nettement, et ce sera pire sur un téléphone.",
  "Très lourde": "La carte est trop lourde pour être agréable : elle saccade fortement, et sera pénible sur un téléphone.",
};

const LAYER_ADVICE: Record<string, { title: string; detail: string }> = {
  "sans décor 3D": {
    title: "Les objets 3D (arbres, promeneurs, véhicules…)",
    detail: "Retirez une partie des objets 3D, surtout les arbres des zones boisées, et réservez promeneurs et animaux animés aux endroits les plus regardés. Couper « Ombres des objets 3D » (réglages de la carte, Ombres et lumière) allège aussi.",
  },
  "sans volumes": {
    title: "Les bâtiments en volume",
    detail: "Mettez une « Hauteur » nulle aux petits bâtiments sans intérêt et simplifiez les contours très détaillés (beaucoup de points). Couper « Ombres des volumes » allège aussi.",
  },
  "sans textures de sol": {
    title: "Les sols texturés (pavés, herbe, sable…)",
    detail: "Dans « Aménagement », gardez un « Sol » texturé et une « Bordure » pour les zones importantes ; ailleurs, une couleur unie suffit.",
  },
  "sans fond OSM": {
    title: "Le fond de carte",
    detail: "Dans les réglages de la carte, décochez dans « Éléments affichés » et « Libellés affichés » ce qui ne sert pas (Végétation, Bâtiments, Noms de rues…) et coupez « Bâtiments en relief ».",
  },
};

function find(results: RenderBenchmarkResult[], view: string, scenario: string, variant = "tout"): RenderBenchmarkResult | undefined {
  return results.find((result) => result.view === view && result.scenario === scenario && result.variant === variant && !result.throttled);
}

/** Réactivité, de 0 à 1 : la moyenne des actions mesurées. `null` sans aucune. */
export function reactivityScore(reactivity: ReactivityMeasure[]): number | null {
  if (!reactivity.length) return null;
  return reactivity.reduce((sum, { id, ms }) => {
    const { fast, slow } = ACTION_LIMITS[id];
    return sum + clamp01(1 - (ms - fast) / (slow - fast));
  }, 0) / reactivity.length;
}

const formatMs = (ms: number) => (ms >= 1000 ? `${(ms / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} s` : `${Math.round(ms)} ms`);
const formatMegabytes = (bytes: number) => `${Math.round(bytes / (1024 * 1024)).toLocaleString("fr-FR")} Mo`;

const formatCount = (value: number) => value.toLocaleString("fr-FR");

/** Ce qui accompagne les gestes : les actions du visiteur chronométrées, la mémoire occupée. */
export interface BenchmarkExtras {
  reactivity?: ReactivityMeasure[];
  memory?: MemoryMeasure | null;
}

/**
 * La note de la carte et ce qu'il faut y changer, à partir du banc d'essai : la note pèse la fluidité
 * de chaque geste, l'arrivée et la réactivité ; les conseils comparent le geste le plus lent toutes
 * couches visibles et avec un groupe de couches masqué, pour désigner ce qui coûte sur cette carte-là.
 */
export function scoreRenderBenchmark(results: RenderBenchmarkResult[], arrival: ArrivalMeasure | null = null, extras: BenchmarkExtras = {}): BenchmarkVerdict {
  let weighted = 0;
  let weights = 0;
  const gestures: number[] = [];
  for (const { view, scenario, weight } of SCENARIO_WEIGHTS) {
    const result = find(results, view, scenario);
    if (!result) continue;
    const value = fluidity(result);
    weighted += value * weight;
    weights += weight;
    gestures.push(value);
  }
  // L'œil retient les pires moments : les gestes les plus saccadés pèsent autant que la moyenne de tous.
  const roughest = gestures.sort((a, b) => a - b).slice(0, WORST_GESTURES);
  const navigation = weights ? (weighted / weights) * (1 - WORST_GESTURES_WEIGHT) + (roughest.reduce((sum, value) => sum + value, 0) / roughest.length) * WORST_GESTURES_WEIGHT : 0;
  const arriving = arrivalScore(arrival);
  const reactivity = extras.reactivity ?? [];
  const reacting = reactivityScore(reactivity);
  const measured: [string, number, number][] = [["Navigation", navigation, PART_WEIGHTS.navigation]];
  if (arriving !== null) measured.push(["Arrivée", arriving, PART_WEIGHTS.arrival]);
  if (reacting !== null) measured.push(["Réactivité", reacting, PART_WEIGHTS.reactivity]);
  const total = measured.reduce((sum, [, , weight]) => sum + weight, 0);
  const score = Math.round((measured.reduce((sum, [, value, weight]) => sum + value * weight, 0) / total) * 100);
  const parts = measured.length > 1 ? measured.map(([label, value]) => ({ label, score: Math.round(value * 100) })) : [];
  const grade = gradeOf(score);

  const impacts: { gain: number; advice: BenchmarkAdvice }[] = [];
  for (const [variant, text] of Object.entries(LAYER_ADVICE)) {
    const without = results.find((result) => result.variant === variant && !result.throttled);
    const all = without && find(results, without.view, without.scenario);
    if (!without || !all) continue;
    const gain = without.fpsMedian - all.fpsMedian;
    if (gain < MEANINGFUL_GAIN_FPS) continue;
    const where = `${all.scenario.toLowerCase()}, ${all.view.toLowerCase()}`;
    const sample = all.decorSample;
    const technical = variant === "sans décor 3D" && sample
      ? `+${gain.toFixed(0)} i/s sans le décor (${where}). ${formatCount(sample.drawCalls)} appels de dessin, ${formatCount(sample.triangles)} triangles, ${formatCount(sample.visibleInstances)} instances, ${formatCount(sample.standalone)} objets seuls, ${formatCount(sample.animated)} animés.`
      : `+${gain.toFixed(0)} i/s quand ce groupe est masqué (${where}, le geste le plus lent).`;
    impacts.push({ gain, advice: { ...text, title: `${text.title} — environ ${gain.toFixed(0)} images/s`, technical } });
  }

  const scatterGesture = find(results, VIEW_CLOSEST, "Déplacement") ?? find(results, VIEW_CLOSE, "Déplacement") ?? find(results, VIEW_START, "Déplacement");
  const sample = scatterGesture?.decorSample;
  // Beaucoup d'éléments semés ne gênent que si le geste où on les a comptés saccade.
  if (sample && sample.scattered > HEAVY_SCATTER && fluidity(scatterGesture!) < SMOOTH_GESTURE) {
    impacts.push({
      gain: MEANINGFUL_GAIN_FPS,
      advice: {
        title: "L'herbe et les cailloux semés sur les sols",
        detail: "Dans « Aménagement » des zones concernées, passez l'« Habillage » de « Dense » à « Léger », ou à « Aucun » sur les grandes zones vues de loin.",
        technical: `${formatCount(sample.scattered)} éléments semés dessinés par image.`,
      },
    });
  }

  const moving = results.filter((result) => result.variant === "tout" && result.scenario !== "Immobile" && !result.throttled);
  const worst = Math.max(0, ...moving.map((result) => result.worstFrameMs));
  const freezes = moving.reduce((sum, result) => sum + result.longTasksMs, 0);
  if (worst > 150 || freezes > 500) {
    const culprit = moving.find((result) => result.worstFrameMs === worst);
    impacts.push({
      gain: 0,
      advice: {
        title: "Des figements ponctuels",
        detail: "La carte se fige par moments une fraction de seconde. Cela ne dépend pas de vos réglages : signalez-le à l'équipe Tokamap si c'est gênant.",
        technical: `Pire image ${worst.toFixed(0)} ms${culprit ? ` (${culprit.scenario.toLowerCase()}, ${culprit.view.toLowerCase()})` : ""} ; ${freezes.toFixed(0)} ms de tâches longues sur les gestes.`,
      },
    });
  }

  const firstApproach = find(results, VIEW_START, FIRST_APPROACH);
  if (firstApproach?.settleMs != null && firstApproach.settleMs > LONG_SETTLE_MS) {
    impacts.push({
      gain: MEANINGFUL_GAIN_FPS,
      advice: {
        title: `Le fond de carte met ${formatMs(firstApproach.settleMs)} à s'afficher de près`,
        detail: "La première fois qu'on s'approche, le fond de carte se télécharge. Dans les réglages de la carte, décocher dans « Éléments affichés » ce qui ne sert pas et couper « Bâtiments en relief » l'allège ; la connexion du visiteur compte aussi.",
        technical: `${firstApproach.settleMs.toFixed(0)} ms entre la fin du premier rapprochement et l'affichage complet des tuiles et images.`,
      },
    });
  }

  impacts.push(...reactivity.filter(({ id, ms }) => ms > ACTION_LIMITS[id].advise).map((action) => ({ gain: MEANINGFUL_GAIN_FPS, advice: reactivityAdvice(action) })));
  impacts.push(...memoryAdvice(extras.memory ?? null));
  impacts.push(...arrivalAdvice(arrival));

  impacts.sort((a, b) => b.gain - a.gain);
  return { score, parts, grade, summary: SUMMARIES[grade], advice: impacts.map(({ advice }) => advice), notes: arrivalNotes(arrival) };
}

const REACTIVITY_DETAILS: Record<ReactivityMeasure["id"], string> = {
  entity: "Un visiteur attend avant de voir la fiche du lieu sur lequel il a cliqué. Cela ne dépend pas de vos réglages : signalez-le à l'équipe Tokamap si c'est gênant.",
  search: "Les résultats de la recherche tardent à chaque lettre tapée : la carte compte beaucoup de lieux. Signalez-le à l'équipe Tokamap si c'est gênant.",
  route: "Le calcul d'un itinéraire d'un bout à l'autre de la carte est long : beaucoup de formes font obstacle. Pour celles qu'on traverse à pied (places, sols, décorations), réglez « Passage » sur « Libre » dans leurs options avancées.",
};

function reactivityAdvice(action: ReactivityMeasure): BenchmarkAdvice {
  return {
    title: `${action.label} — ${formatMs(action.ms)}`,
    detail: REACTIVITY_DETAILS[action.id],
    technical: `${action.ms.toFixed(0)} ms entre l'action et l'image qui en montre le résultat.`,
  };
}

function memoryAdvice(memory: MemoryMeasure | null): { gain: number; advice: BenchmarkAdvice }[] {
  if (!memory) return [];
  const decor = memory.decorBytes ?? 0;
  const heap = memory.jsHeapBytes ?? 0;
  if (decor <= HEAVY_DECOR_BYTES && heap <= HEAVY_HEAP_BYTES) return [];
  return [{
    gain: MEANINGFUL_GAIN_FPS,
    advice: {
      title: `La carte demande beaucoup de mémoire — environ ${formatMegabytes(decor + heap)}`,
      detail: "Sur un iPhone, Safari peut fermer l'onglet au-delà d'environ 1 Go. Les objets 3D très variés et les promeneurs animés sont les plus gourmands : en garder moins de modèles différents allège.",
      technical: `Décor 3D envoyé au GPU ≈ ${formatMegabytes(decor)}${memory.jsHeapBytes === null ? "" : `, mémoire JavaScript ${formatMegabytes(heap)}`}.`,
    },
  }];
}

/** Au-delà de cette part de ressources servies par le cache, la mesure ne vaut pas pour une première visite. */
const WARM_CACHE_SHARE = 0.5;

export function arrivalNotes(arrival: ArrivalMeasure | null): string[] {
  if (!arrival) return [];
  if (arrival.estimate) {
    return ["Arrivée estimée depuis l'éditeur : le document est déjà en mémoire et les ressources souvent en cache. Pour la mesure réelle, utilisez « Tester comme un visiteur »."];
  }
  const cache = arrival.cache;
  if (!cache) return [];
  const share = cache.cached / (cache.cached + cache.fetched);
  if (share <= WARM_CACHE_SHARE) return [];
  return [`${Math.round(share * 100)} % des ressources venaient du cache du navigateur : une première visite sera plus lente. Pour la mesurer, rechargez la page sans le cache : Ctrl+F5 (Cmd+Maj+R sur Mac).`];
}

const formatSeconds = (ms: number) => `${(ms / 1000).toLocaleString("fr-FR", { maximumFractionDigits: 1 })} s`;

function arrivalAdvice(arrival: ArrivalMeasure | null): { gain: number; advice: BenchmarkAdvice }[] {
  const impacts: { gain: number; advice: BenchmarkAdvice }[] = [];
  if (arrival && !arrival.window.throttled && arrival.loadMs > LONG_LOAD_MS) {
    impacts.push({
      gain: MEANINGFUL_GAIN_FPS,
      advice: {
        title: `Le chargement de la carte — ${formatSeconds(arrival.loadMs)}`,
        detail: "La carte met du temps à s'afficher en entier. Le nombre d'objets 3D, de photos et de sols texturés compte le plus, avec la connexion du visiteur ; une seconde visite profite du cache.",
        technical: `${arrival.loadMs.toFixed(0)} ms ${arrival.fromPageStart ? "de l'ouverture de la page" : "de la création de la carte"} à son premier affichage complet${arrival.estimate ? ", estimé depuis l'éditeur" : ""}.`,
      },
    });
  }
  const window = arrival && !arrival.window.throttled ? arrival.window : null;
  if (window && (window.worstFrameMs > 150 || window.longTasksMs > 500)) {
    impacts.push({
      gain: 0,
      advice: {
        title: "Des figements juste après l'ouverture",
        detail: "Les premières secondes, la carte se fige par moments le temps de préparer le décor. Cela ne dépend pas de vos réglages : signalez-le à l'équipe Tokamap si c'est gênant.",
        technical: `Pire image ${window.worstFrameMs.toFixed(0)} ms ; ${window.longTasksMs.toFixed(0)} ms de tâches longues dans les premières secondes${arrival!.flight ? ", vol d'arrivée compris" : ""}.`,
      },
    });
  }
  if (window && arrival!.flight && fluidity(window) < 0.7) {
    impacts.push({
      gain: MEANINGFUL_GAIN_FPS,
      advice: {
        title: "Le vol d'arrivée saccade",
        detail: "La caméra traverse toute la carte pendant que le décor se prépare encore, ce qui demande beaucoup d'un coup. Vous pouvez couper « Vol d'arrivée » dans les réglages de la carte.",
        technical: `${window.fpsMedian.toFixed(0)} i/s médian, ${window.fpsLow.toFixed(0)} i/s au plus lent, pire image ${window.worstFrameMs.toFixed(0)} ms sur les premières secondes.`,
      },
    });
  }
  return impacts;
}
